'use client';

import type { EmploymentTypeFilter, SortKey } from './types';

interface HrDirectoryControlsProps {
  search: string;
  onSearchChange: (s: string) => void;
  filter: EmploymentTypeFilter;
  onFilterChange: (f: EmploymentTypeFilter) => void;
  sortKey: SortKey;
  onSortChange: (s: SortKey) => void;
  departments: string[];
  selectedDepartment: string | null;
  onDepartmentChange: (d: string | null) => void;
  visibleCount: number;
  totalCount: number;
}

const FILTER_TABS: Array<{ key: EmploymentTypeFilter; label: string }> = [
  { key: 'all',        label: 'All' },
  { key: 'active',     label: 'Active' },
  { key: 'contractor', label: 'Contractors' },
  { key: 'terminated', label: 'Terminated' },
];

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'name',       label: 'Name (A-Z)' },
  { key: 'department', label: 'Department' },
  { key: 'hire_date',  label: 'Tenure (Longest first)' },
  { key: 'rating',     label: 'Rating (Highest)' },
];

const HrDirectoryControls = ({
  search, onSearchChange,
  filter, onFilterChange,
  sortKey, onSortChange,
  departments, selectedDepartment, onDepartmentChange,
  visibleCount, totalCount,
}: HrDirectoryControlsProps) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 mb-4 sticky top-[60px] z-20" style={{ backdropFilter: 'blur(8px)' }}>
      {/* Top row: search + sort dropdown */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search by name, role, department…"
            className="w-full pl-8 pr-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
          />
        </div>

        {/* Department dropdown */}
        <div className="relative">
          <select
            value={selectedDepartment ?? ''}
            onChange={e => onDepartmentChange(e.target.value || null)}
            className="pl-3 pr-7 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all appearance-none cursor-pointer min-w-[160px]"
          >
            <option value="">All Departments</option>
            {departments.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Sort dropdown */}
        <div className="relative">
          <select
            value={sortKey}
            onChange={e => onSortChange(e.target.value as SortKey)}
            className="pl-3 pr-7 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all appearance-none cursor-pointer min-w-[180px]"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>Sort: {o.label}</option>
            ))}
          </select>
          <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Bottom row: filter tabs + count */}
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {FILTER_TABS.map(tab => {
            const active = filter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onFilterChange(tab.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${
                  active
                    ? 'bg-[#1C2E5E] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-500">
          {visibleCount === totalCount
            ? `${totalCount} employees`
            : `Showing ${visibleCount} of ${totalCount}`}
        </p>
      </div>
    </div>
  );
};

export default HrDirectoryControls;
